/**
 * Standard UTM campaign fields captured from the signup URL.
 *
 * `campaign` identifies the broader event/campaign and `content` distinguishes
 * a specific post, creative, or link within it. Values are stored without the
 * `utm_` prefix under `marketing.utm` on the user document.
 */
export interface UtmAttribution {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
}

export interface CapturedUtmAttribution extends UtmAttribution {
  capturedAt: string;
}

export const MAX_UTM_VALUE_LENGTH = 1024;

const PENDING_UTM_ATTRIBUTION_KEY = "signupUtmAttribution";
const PENDING_UTM_ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const UTM_FIELDS = [
  ["utm_source", "source"],
  ["utm_medium", "medium"],
  ["utm_campaign", "campaign"],
  ["utm_term", "term"],
  ["utm_content", "content"],
] as const;

function normalizeValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, MAX_UTM_VALUE_LENGTH) : undefined;
}

function normalizeAttribution(value: unknown): UtmAttribution {
  if (!value || typeof value !== "object") return {};

  const record = value as Record<string, unknown>;
  const attribution: UtmAttribution = {};

  UTM_FIELDS.forEach(([, field]) => {
    const normalized = normalizeValue(record[field]);
    if (normalized) attribution[field] = normalized;
  });

  return attribution;
}

function hasAttribution(attribution: UtmAttribution): boolean {
  return Object.keys(attribution).length > 0;
}

function sameAttribution(
  first: UtmAttribution,
  second: UtmAttribution
): boolean {
  return UTM_FIELDS.every(([, field]) => first[field] === second[field]);
}

export function clearPendingUtmAttribution(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(PENDING_UTM_ATTRIBUTION_KEY);
  } catch {
    // Attribution is best-effort and must never make signup unavailable.
  }
}

export function getPendingUtmAttribution(): CapturedUtmAttribution | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem(PENDING_UTM_ATTRIBUTION_KEY);
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const capturedAt =
      typeof parsed?.capturedAt === "string" ? parsed.capturedAt : undefined;
    const capturedAtMs = capturedAt ? Date.parse(capturedAt) : Number.NaN;
    const attribution = normalizeAttribution(parsed?.attribution);

    if (
      !capturedAt ||
      !Number.isFinite(capturedAtMs) ||
      Date.now() - capturedAtMs > PENDING_UTM_ATTRIBUTION_MAX_AGE_MS ||
      !hasAttribution(attribution)
    ) {
      clearPendingUtmAttribution();
      return undefined;
    }

    return { ...attribution, capturedAt };
  } catch {
    clearPendingUtmAttribution();
    return undefined;
  }
}

/** Return only non-empty UTM values from the current browser URL. */
export function getUtmAttribution(): UtmAttribution {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const attribution: UtmAttribution = {};

  UTM_FIELDS.forEach(([queryParam, field]) => {
    const value = normalizeValue(params.get(queryParam));
    if (value) attribution[field] = value;
  });

  return attribution;
}

/**
 * Capture the current signup URL, or recover the most recent valid capture.
 *
 * Signup has supported links to Login and Terms that drop the query string.
 * Keeping a bounded copy ensures the campaign/post survives those detours. A
 * new attributed URL replaces the whole bundle so separate campaigns cannot
 * be mixed together.
 */
export function captureUtmAttribution(): CapturedUtmAttribution | undefined {
  if (typeof window === "undefined") return undefined;

  const current = getUtmAttribution();
  const pending = getPendingUtmAttribution();
  if (!hasAttribution(current)) return pending;
  if (pending && sameAttribution(current, pending)) return pending;

  const captured: CapturedUtmAttribution = {
    ...current,
    capturedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(
      PENDING_UTM_ATTRIBUTION_KEY,
      JSON.stringify({ attribution: current, capturedAt: captured.capturedAt })
    );
  } catch {
    // See clearPendingUtmAttribution.
  }

  return captured;
}
